import {
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material';
import type { Provider, Account } from '@repo/api-gw-sdk';
import React from 'react';

import { CloudProviders } from '@/data/inventory/data';

import { EmptySelect } from '../regions/emptySelect';
import SelectPlaceholder from '../selectPlaceholder';

export type VaultManagedType = 'eon-managed' | `self-managed-${Provider}`;

export const VaultManagedTypeSelect = ({
  title,
  selfManagedVaultAccounts,
  showEonManaged,
  restrictedCloudProviders,
  region,
  regionRequired,
  vaultManagedType,
  onChange,
}: {
  title: string;
  selfManagedVaultAccounts: Account[];
  showEonManaged: boolean;
  restrictedCloudProviders: Provider[];
  region?: string;
  regionRequired: boolean;
  vaultManagedType?: VaultManagedType;
  onChange: (vaultManagedType: VaultManagedType) => void;
}) => {
  if (!selfManagedVaultAccounts.length || (regionRequired && !region)) {
    return <EmptySelect title={title} size='medium' />;
  }

  const onSelect = (event: SelectChangeEvent<VaultManagedType>) => {
    onChange(event.target.value as VaultManagedType);
  };

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Select
        sx={{ height: '48px', paddingLeft: '8px' }}
        value={vaultManagedType || ''}
        onChange={onSelect}
        displayEmpty
        renderValue={(value: string) => {
          if (!value) {
            return <SelectPlaceholder />;
          }
          return getLabel(value as VaultManagedType);
        }}
      >
        {showEonManaged && (
          <MenuItem key='eon-managed' value='eon-managed'>
            Eon-managed (recommended)
          </MenuItem>
        )}
        {Object.values(restrictedCloudProviders).map((cloudProvider) => {
          const disabled =
            regionRequired &&
            !!region &&
            selfManagedVaultAccounts.every(
              (account) => !account.regions?.includes(region)
            );

          const menuItem = (
            <MenuItem
              disabled={disabled}
              key={`self-managed-${cloudProvider}`}
              value={`self-managed-${cloudProvider}`}
            >
              {getLabel(`self-managed-${cloudProvider}`)}
            </MenuItem>
          );

          return disabled ? (
            <Tooltip
              key={`self-managed-${cloudProvider}`}
              title='No available vault accounts in the selected region'
            >
              <span>{menuItem}</span>
            </Tooltip>
          ) : (
            menuItem
          );
        })}
      </Select>
    </FormControl>
  );
};

const getLabel = (vaultType: VaultManagedType) => {
  if (vaultType === 'eon-managed') {
    return 'Eon-managed (recommended)';
  }
  return `Self-managed in ${CloudProviders[vaultType.split('self-managed-')[1] as Provider].displayName}`;
};
